import React, { useState, useEffect, useCallback } from 'react';
import GameHUD from '../shared/GameHUD';
import PauseMenu from '../shared/PauseMenu';
import GameComplete from '../GameComplete';
import { calculateRewards } from '../../../services/gameService';
import './SpeedQuizGame.css';

function SpeedQuizGame({
    gameContent,
    template,
    topic,
    difficulty = 'intermediate',
    onComplete,
    onQuit
}) {
    // Game state
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [correctAnswers, setCorrectAnswers] = useState(0);
    const [racePosition, setRacePosition] = useState(0);
    const [timeLeft, setTimeLeft] = useState(10);
    const [totalTime, setTotalTime] = useState(0);
    const [streak, setStreak] = useState(0);
    const [bestStreak, setBestStreak] = useState(0);
    const [xpEarned, setXpEarned] = useState(0);
    const [coinsEarned, setCoinsEarned] = useState(0);
    const [speed, setSpeed] = useState(0);

    // Question state
    const [selectedAnswer, setSelectedAnswer] = useState(null);
    const [showResult, setShowResult] = useState(false);
    const [isCorrect, setIsCorrect] = useState(false);

    // UI state
    const [isPaused, setIsPaused] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const [victory, setVictory] = useState(false);
    const [showCountdown, setShowCountdown] = useState(true);
    const [countdown, setCountdown] = useState(3);

    // Questions
    const questions = gameContent?.questions || gameContent?.rounds?.[0]?.questions || [];
    const totalQuestions = questions.length;
    const currentQuestion = questions[currentQuestionIndex];

    // Initial countdown
    useEffect(() => {
        if (!showCountdown) return;

        const timer = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    setShowCountdown(false);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [showCountdown]);

    // Update speed based on streak
    useEffect(() => {
        setSpeed(50 + (streak * 10));
    }, [streak]);

    const handleGameOver = useCallback((won) => {
        setVictory(won);
        setIsComplete(true);

        const accuracy = totalQuestions > 0
            ? Math.round((correctAnswers / totalQuestions) * 100)
            : 0;

        const rewards = calculateRewards(template, correctAnswers, accuracy);

        setXpEarned(rewards.xp);
        setCoinsEarned(rewards.coins);

        if (onComplete) {
            onComplete({
                victory: won,
                score,
                xp: rewards.xp,
                coins: rewards.coins,
                stars: rewards.stars,
                accuracy,
                stats: {
                    questionsAnswered: correctAnswers,
                    bestStreak,
                    totalTime,
                    avgTimePerQuestion: totalTime > 0 ? (totalTime / totalQuestions).toFixed(1) : 0
                }
            });
        }
    }, [score, correctAnswers, totalQuestions, bestStreak, totalTime, template, onComplete]);

    const moveToNextQuestion = useCallback(() => {
        if (currentQuestionIndex + 1 < totalQuestions) {
            setCurrentQuestionIndex(prev => prev + 1);
            setShowResult(false);
            setSelectedAnswer(null);
            setTimeLeft(template?.difficultyScaling?.[difficulty]?.timePerQuestion || 10);
        } else {
            handleGameOver(correctAnswers >= totalQuestions * 0.5);
        }
    }, [currentQuestionIndex, totalQuestions, template, difficulty, handleGameOver, correctAnswers]);

    const handleTimeout = useCallback(() => {
        setSelectedAnswer(null);
        setIsCorrect(false);
        setShowResult(true);
        setStreak(0);

        setTimeout(() => moveToNextQuestion(), 1500);
    }, [moveToNextQuestion]);

    // Question timer
    useEffect(() => {
        if (isPaused || isComplete || showResult || showCountdown || !currentQuestion) return;

        const timer = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    handleTimeout();
                    return 0;
                }
                return prev - 1;
            });
            setTotalTime(prev => prev + 1);
        }, 1000);

        return () => clearInterval(timer);
    }, [isPaused, isComplete, showResult, showCountdown, currentQuestion, handleTimeout]);

    const handleAnswer = (answer) => {
        if (showResult || !currentQuestion) return;

        setSelectedAnswer(answer);
        const correct = answer === currentQuestion.correctAnswer;
        setIsCorrect(correct);
        setShowResult(true);

        if (correct) {
            const timeBonus = Math.floor(timeLeft * 10);
            const streakBonus = streak * 15;
            setScore(prev => prev + 100 + timeBonus + streakBonus);
            setCorrectAnswers(prev => prev + 1);
            setStreak(prev => {
                const newStreak = prev + 1;
                if (newStreak > bestStreak) setBestStreak(newStreak);
                return newStreak;
            });
            setXpEarned(prev => prev + 10 + Math.floor(timeBonus / 10));

            // Move race position forward
            setRacePosition(prev => Math.min(100, prev + (100 / totalQuestions)));
        } else {
            setStreak(0);
        }

        setTimeout(() => moveToNextQuestion(), 1200);
    };

    const handleRestart = () => {
        setCurrentQuestionIndex(0);
        setScore(0);
        setCorrectAnswers(0);
        setRacePosition(0);
        setTimeLeft(template?.difficultyScaling?.[difficulty]?.timePerQuestion || 10);
        setTotalTime(0);
        setStreak(0);
        setBestStreak(0);
        setXpEarned(0);
        setCoinsEarned(0);
        setSelectedAnswer(null);
        setShowResult(false);
        setIsPaused(false);
        setIsComplete(false);
        setVictory(false);
        setShowCountdown(true);
        setCountdown(3);
    };

    const getOptionClass = (option) => {
        if (!showResult) {
            return selectedAnswer === option ? 'selected' : '';
        }
        if (option === currentQuestion?.correctAnswer) return 'correct';
        if (option === selectedAnswer && !isCorrect) return 'incorrect';
        return 'dimmed';
    };

    if (!gameContent) {
        return (
            <div className="speedquiz-loading">
                <div className="loading-spinner">🏎️</div>
                <p>Starting your engines...</p>
            </div>
        );
    }

    // Countdown screen
    if (showCountdown) {
        return (
            <div className="speedquiz-countdown">
                <div className="countdown-content">
                    <h2>Get Ready!</h2>
                    <div className="countdown-number">{countdown}</div>
                    <p>Race through questions as fast as you can!</p>
                </div>
            </div>
        );
    }

    return (
        <div className="speedquiz-game">
            <GameHUD
                gameName="Speed Race"
                score={score}
                timeLeft={timeLeft}
                combo={streak}
                xp={xpEarned}
                coins={coinsEarned}
                onPause={() => setIsPaused(true)}
                showLives={false}
            />

            {/* Race Track */}
            <div className="race-track">
                <div className="track-progress">
                    <div className="track-line">
                        <div className="checkpoints">
                            {[...Array(5)].map((_, i) => (
                                <div
                                    key={i}
                                    className={`checkpoint ${racePosition >= ((i + 1) * 20) ? 'passed' : ''}`}
                                >
                                    🏁
                                </div>
                            ))}
                        </div>
                        <div
                            className="racer"
                            style={{ left: `${racePosition}%` }}
                        >
                            🏎️
                        </div>
                    </div>
                </div>
                <div className="speed-display">
                    <span className="speed-value">{speed}</span>
                    <span className="speed-unit">km/h</span>
                </div>
            </div>

            <div className="speedquiz-info">
                <span className="speedquiz-topic">📚 {topic}</span>
                <span className="speedquiz-progress">
                    Question {currentQuestionIndex + 1} / {totalQuestions}
                </span>
            </div>

            {/* Timer Bar */}
            <div className="speed-timer">
                <div
                    className="timer-fill"
                    style={{
                        width: `${(timeLeft / (template?.difficultyScaling?.[difficulty]?.timePerQuestion || 10)) * 100}%`,
                        backgroundColor: timeLeft > 5 ? '#4CAF50' : timeLeft > 2 ? '#ffc107' : '#f44336'
                    }}
                />
            </div>

            {/* Question */}
            {currentQuestion && (
                <div className="speedquiz-question">
                    <h3 className="question-text">{currentQuestion.question}</h3>

                    <div className="options-row">
                        {currentQuestion.options.map((option, index) => (
                            <button
                                key={index}
                                className={`speed-option ${getOptionClass(option)}`}
                                onClick={() => handleAnswer(option)}
                                disabled={showResult}
                            >
                                <span className="option-key">{index + 1}</span>
                                <span className="option-text">{option}</span>
                            </button>
                        ))}
                    </div>

                    {showResult && (
                        <div className={`result-flash ${isCorrect ? 'correct' : 'incorrect'}`}>
                            {isCorrect ? (
                                <>🚀 +{100 + Math.floor(timeLeft * 10)} points!</>
                            ) : (
                                <>❌ Wrong! Answer: {currentQuestion.correctAnswer}</>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Streak Display */}
            {streak > 1 && (
                <div className="streak-display">
                    <span className="streak-fire">🔥</span>
                    <span className="streak-count">{streak}x STREAK!</span>
                    <span className="streak-bonus">+{streak * 15} bonus</span>
                </div>
            )}

            {/* Pause Menu */}
            {isPaused && (
                <PauseMenu
                    gameName="Speed Race"
                    score={score}
                    stats={{
                        accuracy: totalQuestions > 0 ? Math.round((correctAnswers / (currentQuestionIndex + 1)) * 100) : 0,
                        combo: bestStreak,
                        timeElapsed: totalTime
                    }}
                    onResume={() => setIsPaused(false)}
                    onRestart={handleRestart}
                    onQuit={onQuit}
                />
            )}

            {/* Game Complete */}
            {isComplete && (
                <GameComplete
                    victory={victory}
                    gameName="Speed Race"
                    score={score}
                    xpEarned={xpEarned}
                    coinsEarned={coinsEarned}
                    stars={correctAnswers === totalQuestions ? 3 : correctAnswers >= totalQuestions * 0.7 ? 2 : 1}
                    accuracy={totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0}
                    stats={{
                        questionsAnswered: correctAnswers,
                        bestStreak,
                        totalTime
                    }}
                    onPlayAgain={handleRestart}
                    onBackToMap={onQuit}
                    topic={topic}
                />
            )}
        </div>
    );
}

export default SpeedQuizGame;
